/**
 * Format currency value
 * @param value - The value to format
 * @param currencyCode - The currency code (default: GBP)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number, currencyCode = 'GBP'): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
  }).format(value);
};

/**
 * Format date string
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', options).format(date);
};

/**
 * Truncate text to a specified length
 * @param text - The text to truncate
 * @param maxLength - The maximum length (default: 50)
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength = 50): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Calculate days remaining until a date
 * @param dateString - The target date string
 * @returns Number of days remaining (negative if in the past)
 */
export const daysRemaining = (dateString: string): number => {
  const targetDate = new Date(dateString);
  const today = new Date();
  
  // Reset time to midnight for accurate day calculation
  targetDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  
  const diffTime = targetDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get status color based on account status
 * @param status - The account status
 * @returns Color code for the status
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Active':
      return '#4caf50'; // Green
    case 'Overdue':
      return '#ff9800'; // Orange
    case 'Suspended':
      return '#f44336'; // Red
    case 'Closed':
      return '#9e9e9e'; // Grey
    default:
      return '#2196f3'; // Blue
  }
};

/**
 * Calculate available credit percentage
 * @param availableCredit - Available credit amount
 * @param creditLimit - Total credit limit
 * @returns Percentage of available credit
 */
export const calculateCreditPercentage = (
  availableCredit: number,
  creditLimit: number
): number => {
  if (creditLimit === 0) return 0;
  return Math.round((availableCredit / creditLimit) * 100);
};

/**
 * Generate a random ID
 * @param prefix - Prefix for the ID (default: 'id')
 * @returns Random ID string
 */
export const generateId = (prefix = 'id'): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Generate a random password without symbols
 * @param length - Length of the password (default: 10)
 * @returns Random password string
 */
export const generateRandomPassword = (length = 10): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};
